import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    TableBody,
    TableRow,
    TableCell,
    Box,
    Checkbox,
    Typography,
    IconButton,
    Tooltip,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import BlockIcon from "@mui/icons-material/Block";
import { formatDate } from "../../../../utils/formatDate";

const TableContent = ({
    pagesList,
    prevPagesList,
    paginatedPages,
    checkedPages,
    selectedScanStatus,
    handleCheckboxChange,
    handleOpenDeleteDialog,
}) => {
    const [newPages, setNewPages] = useState([]);
    const [isProjectChanged, setIsProjectChanged] = useState(false);
    const [tooltipOpen, setTooltipOpen] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        setIsProjectChanged(
            !prevPagesList.every((prevPage) =>
                pagesList.some((page) => page.id === prevPage.id)
            )
        );

        if (
            pagesList.length &&
            prevPagesList.length &&
            pagesList[0].project_id === prevPagesList[0].project_id
        ) {
            const newPageIds = pagesList
                .filter(
                    (page) =>
                        !prevPagesList.some(
                            (prevPage) => prevPage.id === page.id
                        )
                )
                .map((page) => page.id);
            setNewPages(newPageIds);
        }
    }, [pagesList, prevPagesList]);

    const handleRowClick = (pageId) => {
        navigate(`/pages/${pageId}`);
    };

    const handleCopyToClipboard = (pageUrl, pageId) => {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(pageUrl);
        } else {
            const textArea = document.createElement("textarea");
            textArea.value = pageUrl;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand("copy");
            document.body.removeChild(textArea);
        }

        setTooltipOpen(pageId);
        setTimeout(() => {
            setTooltipOpen("");
        }, 1000);
    };

    const handleTooltipClose = () => {
        setTooltipOpen(false);
    };

    return paginatedPages.length ? (
        <TableBody>
            {paginatedPages.map((page) => {
                console.log("page", page);
                let isNew;
                if (isProjectChanged) {
                    isNew = false;
                } else {
                    isNew = newPages.includes(page.id);
                }

                const isChecked = checkedPages.includes(page.id);

                return (
                    <TableRow
                        key={page.id}
                        className="pagesTableBodyRow"
                        hover
                        onClick={() => handleRowClick(page.id)}
                        sx={{
                            backgroundColor:
                                isNew || isChecked
                                    ? "rgba(69, 116, 210, 0.1)"
                                    : "inherit",
                        }}
                    >
                        <TableCell
                            className="pagesTableBodyCheckboxCell"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Checkbox
                                className="pagesTableBodyCheckbox"
                                checked={checkedPages.includes(page.id)}
                                onChange={() => handleCheckboxChange(page.id)}
                            />
                        </TableCell>
                        <TableCell className="pagesTableBodyCell">
                            {page.label || ""}
                        </TableCell>
                        <TableCell className="pagesTableBodyCell">
                            <Box className="pageUrl">
                                {page.url}
                                <Tooltip
                                    PopperProps={{
                                        disablePortal: true,
                                        sx: {
                                            "& .MuiTooltip-tooltip": {
                                                backgroundColor: "primary.main",
                                            },
                                        },
                                    }}
                                    onClose={handleTooltipClose}
                                    open={tooltipOpen === page.id}
                                    disableFocusListener
                                    disableHoverListener
                                    disableTouchListener
                                    title="Copied to clipboard!"
                                >
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopyToClipboard(
                                                page.url,
                                                page.id
                                            );
                                        }}
                                    >
                                        <ContentCopyIcon
                                            color="primary"
                                            fontSize="small"
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </TableCell>
                        <TableCell className="pagesTableBodyCell">
                            {page.created_at
                                ? formatDate(page.created_at)
                                : "N/A"}
                        </TableCell>
                        <TableCell className="pagesTableBodyCell">
                            {page.last_scanned_at
                                ? formatDate(page.last_scanned_at)
                                : "Unscanned"}
                        </TableCell>
                        <TableCell className="pagesTableBodyCell pageTests">
                            {page.passed_ui_test_count ||
                            page.failed_ui_test_count ? (
                                <Box className="pageTestsContentContainer">
                                    {page.passed_ui_test_count ? (
                                        <Box className="pagePassedTests">
                                            <Typography color="success.main">
                                                {page.passed_ui_test_count}
                                            </Typography>
                                        </Box>
                                    ) : null}
                                    {page.failed_ui_test_count ? (
                                        <Box className="pageFailedTests">
                                            <Typography color="error.main">
                                                {page.failed_ui_test_count}
                                            </Typography>
                                        </Box>
                                    ) : null}
                                </Box>
                            ) : (
                                <Typography>-</Typography>
                            )}
                        </TableCell>
                        <TableCell className="pagesTableBodyCell testCasesColumn">
                            {(() => {
                                const isUnsupported = page?.pageTypes ?
                                    page?.pageTypes[0].type === "Unknown" : false;
                                return isUnsupported ? (
                                    <Tooltip
                                        title="Page Types is not supported"
                                        PopperProps={{
                                            sx: {
                                                "& .MuiTooltip-tooltip": {
                                                    backgroundColor:
                                                        "primary.main",
                                                },
                                            },
                                        }}
                                    >
                                        <BlockIcon sx={{ color: "#FF9800" }} />
                                    </Tooltip>
                                ) : (
                                    page?.testCasesTotal ?? 0
                                );
                            })()}
                        </TableCell>
                        <TableCell className="pagesTableBodyCell pageActions">
                            <IconButton
                                className="pagesActionsIconContainer"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenDeleteDialog(page?.id);
                                }}
                            >
                                <DeleteOutlineOutlinedIcon color="error" />
                            </IconButton>
                        </TableCell>
                    </TableRow>
                );
            })}
        </TableBody>
    ) : pagesList.length &&
      selectedScanStatus === "Scanned" &&
      !paginatedPages.length ? (
        <TableBody>
            <TableRow>
                <TableCell colSpan={8}>
                    <Typography variant="body1" align="center">
                        No data available yet! Please select the page(s) you
                        want to scan and click the corresponding button to get
                        started.
                    </Typography>
                </TableCell>
            </TableRow>
        </TableBody>
    ) : pagesList.length &&
      selectedScanStatus === "Unscanned" &&
      !paginatedPages.length ? (
        <TableBody>
            <TableRow>
                <TableCell colSpan={8}>
                    <Typography variant="body1" align="center">
                        <strong>All pages scanned!</strong>
                        <br />
                        It looks like you’ve scanned all the discovered pages.
                        You can manually add new pages by clicking the
                        corresponding button.
                    </Typography>
                </TableCell>
            </TableRow>
        </TableBody>
    ) : (
        <TableBody>
            <TableRow>
                <TableCell colSpan={8}>
                    <Typography variant="body1" align="center">
                        Website Mapping is nearly ready, so you’ll soon be able
                        to view and select pages for scanning.
                    </Typography>
                </TableCell>
            </TableRow>
        </TableBody>
    );
};

export default TableContent;
